<script setup lang="ts">
/**
 * PaperLoader — the cat walks by: her paw-print cut-outs appear one after
 * another along their drawn trail, then fade, and the walk starts again.
 * Replaces spinners / `Loader2` / blank waits. Under prefers-reduced-motion the
 * whole trail is shown static. Always announces itself (role=status).
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCutout } from '@app/utils/paperCutouts';

const props = withDefaults(defineProps<{ size?: 'sm' | 'md' | 'lg'; label?: string }>(), { size: 'md', label: undefined });

const { t } = useI18n();
const trail = getCutout('paw_prints');
const WIDTH = { sm: 72, md: 120, lg: 180 } as const;
const STEP_MS = 180;
const CYCLE_MS = trail.parts.length * STEP_MS + 900;

// The walk is phase-locked to a shared clock, so two loaders shown back to back
// (e.g. "fetching cards" then "preloading images") read as one continuous walk.
const phase = typeof performance !== 'undefined' ? performance.now() % CYCLE_MS : 0;

// The drawing's trail runs left → right; step through the prints in that order.
const prints = computed(() =>
  [...trail.parts]
    .sort((a, b) => a.x - b.x)
    .map((p, i) => ({
      ...p,
      style: {
        left: `${(p.x / trail.width) * 100}%`,
        top: `${(p.y / trail.height) * 100}%`,
        width: `${(p.w / trail.width) * 100}%`,
        animationDelay: `${i * STEP_MS - phase}ms`,
        animationDuration: `${CYCLE_MS}ms`,
      },
    })),
);
const width = computed(() => WIDTH[props.size]);
const text = computed(() => props.label ?? t('loading'));
</script>

<template>
  <div
    class="paper-loader flex flex-col items-center gap-3"
    role="status"
    aria-live="polite"
  >
    <div
      class="relative"
      :style="{ width: `${width}px`, height: `${(width * trail.height) / trail.width}px` }"
      aria-hidden="true"
    >
      <img
        v-for="p in prints"
        :key="p.url"
        :src="p.url"
        alt=""
        draggable="false"
        class="pl-print"
        :style="p.style"
      >
    </div>
    <span
      v-if="label"
      class="type-caption text-center"
    >{{ text }}</span>
    <span
      v-else
      class="sr-only"
    >{{ text }}</span>
  </div>
</template>

<style scoped>
.pl-print {
  position: absolute;
  display: block;
  height: auto;
  opacity: 0;
  animation-name: pl-step;
  animation-timing-function: ease-out;
  animation-iteration-count: infinite;
  user-select: none;
  -webkit-user-drag: none;
  filter: drop-shadow(0 2px 2px var(--paper-shade));
}
/* A print lands (small press into the paper), stays while the rest of the walk
   happens, then the whole trail fades together. */
@keyframes pl-step {
  0% { opacity: 0; transform: translateY(-3px) scale(0.85); }
  8% { opacity: 1; transform: none; }
  78% { opacity: 1; transform: none; }
  100% { opacity: 0; transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .pl-print {
    animation: none;
    opacity: 1;
  }
}
</style>
